import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Alert, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Box } from '@mui/material';
import { getDriveAccessToken } from '../api';

const GooglePicker = forwardRef(({ onFolderSelected }, ref) => {
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const [error, setError] = useState('');
  const [accessToken, setAccessToken] = useState(null);
  const [createFolderDialog, setCreateFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Expose openPicker function to parent component
  useImperativeHandle(ref, () => ({
    openPicker: async () => {
      if (!pickerLoaded) {
        setError('Google Picker is not ready yet. Please wait a moment and try again.');
        return;
      }
      if (!accessToken) {
        setError('Google Drive authentication is required. Please connect to Google Drive first.');
        return;
      }
      handleOpenPickerInternal();
    },
    createFolder: async () => {
      if (!pickerLoaded || !accessToken) {
        setError('Google Picker is not ready yet. Please wait a moment and try again.');
        return;
      }
      setCreateFolderDialog(true);
    }
  }));

  useEffect(() => {
    // Load Google Picker API and get access token immediately
    const loadPickerAndToken = async () => {
      try {
        // Load Google API script if not already loaded
        if (!window.gapi) {
          const script = document.createElement('script');
          script.src = 'https://apis.google.com/js/api.js';
          script.onload = () => {
            window.gapi.load('picker', async () => {
              setPickerLoaded(true);
              // Get access token immediately
              try {
                const tokenResponse = await getDriveAccessToken();
                setAccessToken(tokenResponse.access_token);
              } catch (err) {
                console.error('Failed to get access token:', err);
                setError('Failed to get access token: ' + err.message);
              }
            });
          };
          script.onerror = () => {
            setError('Failed to load Google Picker API');
          };
          document.head.appendChild(script);
        } else {
          window.gapi.load('picker', async () => {
            setPickerLoaded(true);
            // Get access token immediately
            try {
              const tokenResponse = await getDriveAccessToken();
              setAccessToken(tokenResponse.access_token);
            } catch (err) {
              console.error('Failed to get access token:', err);
              setError('Failed to get access token: ' + err.message);
            }
          });
        }
      } catch (err) {
        setError('Failed to initialize Google Picker: ' + err.message);
      }
    };

    loadPickerAndToken();
  }, []);

  const handleOpenPickerInternal = () => {
    try {
      // Create a DocsView that starts in My Drive and includes folders
      const docsView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true);

      // Create and show the picker
      const picker = new window.google.picker.PickerBuilder()
        .setOAuthToken(accessToken)
        .setCallback(handlePickerCallback)
        .addView(docsView)
        .setSelectableMimeTypes('application/vnd.google-apps.folder')
        .enableFeature(window.google.picker.Feature.NAV_HIDDEN)
        .setSize(1051, 650)
        .build();

      picker.setVisible(true);
    } catch (err) {
      setError('Failed to open Google Drive picker: ' + err.message);
    }
  };

  const handlePickerCallback = (data) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const folder = data.docs[0];
      onFolderSelected({
        id: folder.id,
        name: folder.name,
        url: folder.url,
        iconUrl: folder.iconUrl
      });
    } else if (data.action === window.google.picker.Action.CANCEL) {
      // User cancelled the picker - clear any errors
      setError('');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Please enter a folder name');
      return;
    }

    setCreatingFolder(true);
    setError('');

    try {
      // Create folder using Google Drive API
      const response = await fetch('https://www.googleapis.com/drive/v3/files', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newFolderName.trim(),
          mimeType: 'application/vnd.google-apps.folder'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to create folder: ${response.statusText}`);
      }

      const folder = await response.json();
      
      // Call the callback with the newly created folder
      onFolderSelected({
        id: folder.id,
        name: folder.name,
        url: `https://drive.google.com/drive/folders/${folder.id}`,
        iconUrl: 'https://drive-thirdparty.googleusercontent.com/16/type/application/vnd.google-apps.folder'
      });

      // Close dialog and reset state
      setCreateFolderDialog(false);
      setNewFolderName('');
    } catch (err) {
      setError('Failed to create folder: ' + err.message);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleCancelCreateFolder = () => {
    setCreateFolderDialog(false);
    setNewFolderName('');
    setError('');
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Create Folder Dialog */}
      <Dialog 
        open={createFolderDialog} 
        onClose={handleCancelCreateFolder}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Folder Name"
              fullWidth
              variant="outlined"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !creatingFolder) {
                  handleCreateFolder();
                }
              }}
              disabled={creatingFolder}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelCreateFolder}
            disabled={creatingFolder}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateFolder}
            variant="contained"
            disabled={creatingFolder || !newFolderName.trim()}
          >
            {creatingFolder ? 'Creating...' : 'Create Folder'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* No button here - parent component will handle the button */}
    </>
  );
});

GooglePicker.displayName = 'GooglePicker';

export default GooglePicker;
